import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Film, Search, Video } from "lucide-react";
import { API_BASE_URL } from "../../../../../api";
import { getMoocCapsulesRequest } from "../../../../redux/actions/capsuleActions";
import CapsuleVideoModal from "./components/CapsuleVideoModal";
import "../RessourcesStyles/MoocManagement.css";

const getCapsuleImageUrl = (capsule) => {
    if (!capsule) return null;
    if (capsule.imageUrl) {
        return capsule.imageUrl.startsWith("http") ? capsule.imageUrl : `${API_BASE_URL}${capsule.imageUrl}`;
    }
    return capsule.picture ? `${API_BASE_URL}/api/formations/capsules/${capsule.id}/image` : null;
};

export default function MoocManagement() {
    const dispatch = useDispatch();
    const { moocCapsules = [], loading } = useSelector((state) => state.capsules || {});

    const [query, setQuery] = useState("");
    const [selectedCapsule, setSelectedCapsule] = useState(null);

    useEffect(() => {
        dispatch(getMoocCapsulesRequest());
    }, [dispatch]);

    const capsulesList = useMemo(() => (Array.isArray(moocCapsules) ? moocCapsules : []), [moocCapsules]);

    const filteredCapsules = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return capsulesList;

        return capsulesList.filter((capsule) =>
            `${capsule.nom || ""} ${capsule.reference || ""} ${capsule.domaineNom || ""} ${capsule.themeNom || ""}`
                .toLowerCase()
                .includes(q)
        );
    }, [capsulesList, query]);

    return (
        <div className="mooc-management-container">
            <div className="cat-header mooc-header">
                <div>
                    <h2>MOOC (Massive open online course)</h2>
                    <p>Capsules publiees dans le catalogue MOOC.</p>
                </div>
            </div>

            <div className="cat-card mooc-card-shell">
                <div className="cat-body">
                    <div className="cat-section-top">
                        <div className="cat-search-wrap mooc-search-bar">
                            <Search size={14} className="cat-search-icon" />
                            <input
                                type="text"
                                className="cat-search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Rechercher par nom, reference, domaine ou theme..."
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="cat-loading">Chargement...</div>
                    ) : filteredCapsules.length === 0 ? (
                        <div className="cat-empty-cell">Aucune capsule MOOC trouvee</div>
                    ) : (
                        <div className="mooc-cards-grid">
                            {filteredCapsules.map((capsule) => {
                                const imageUrl = getCapsuleImageUrl(capsule);

                                return (
                                    <article key={capsule.id} className="mooc-card">
                                        <div
                                            className="mooc-card-cover"
                                            onClick={() => setSelectedCapsule(capsule)}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            {imageUrl ? (
                                                <img className="mooc-card-cover-img" src={imageUrl} alt={capsule.nom || "Capsule"} />
                                            ) : (
                                                <div className="mooc-card-cover-placeholder">
                                                    <Film size={34} />
                                                    <span>Capsule video</span>
                                                </div>
                                            )}
                                            <div className="mooc-card-media-badge">
                                                <Video size={14} />
                                                Capsule video
                                            </div>
                                        </div>

                                        <div className="mooc-card-body">
                                            <div className="mooc-card-ref">{capsule.reference || "-"}</div>
                                            <h3 className="mooc-card-title">{capsule.nom || "Capsule"}</h3>

                                            <div className="mooc-card-meta">
                                                <div><span>Domaine :</span> {capsule.domaineNom || "-"}</div>
                                                <div><span>Theme :</span> {capsule.themeNom || "-"}</div>
                                            </div>

                                            <p className="mooc-card-description">
                                                {capsule.description || "Aucune description disponible."}
                                            </p>

                                            <div className="mooc-card-actions">
                                                <button
                                                    type="button"
                                                    className="mooc-btn-primary"
                                                    onClick={() => setSelectedCapsule(capsule)}
                                                >
                                                    Voir
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <CapsuleVideoModal
                isOpen={Boolean(selectedCapsule)}
                capsule={selectedCapsule}
                onClose={() => setSelectedCapsule(null)}
            />
        </div>
    );
}
